import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import PublicLayout from "@/components/layout/PublicLayout";
import { toast } from "sonner";

const ChangePassword = () => {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error(t("phase2.passwordTooShort")); return; }
    if (next !== confirm) { toast.error(t("phase2.passwordsMismatch")); return; }
    if (next === current) { toast.error(t("phase2.newPasswordDifferent")); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(t("auth.passwordChanged"));
      const u = await refresh();
      // Send the user to the panel that matches their role. qa_manager has
      // limited admin access; qa_tester + regular users go to /app.
      // Institutional Observer is read-only and lands directly on the
      // advanced statistics dashboard (the only meaningful stop for them).
      if (u && u.role === "institutional_observer") {
        navigate("/admin/stats-advanced", { replace: true });
      } else if (u && (u.role === "admin" || u.role === "staff" || u.role === "qa_manager")) {
        navigate("/admin", { replace: true });
      } else {
        navigate("/app", { replace: true });
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="tsw-section py-16 max-w-md">
        <div className="tsw-overline">{t("auth.changePassword")}</div>
        <h1 className="text-3xl font-display font-bold text-slate-900 mt-1" data-testid="change-password-heading">
          {t("auth.changePassword")}
        </h1>
        {user?.must_change_password && (
          <p className="text-slate-600 mt-2" data-testid="change-password-must">
            {t("auth.mustChange")}
          </p>
        )}

        <form
          onSubmit={submit}
          className="tsw-card p-6 sm:p-8 space-y-5 mt-6"
          data-testid="change-password-form"
        >
          {user?.email && (
            <div className="text-xs text-slate-500" data-testid="cp-email">
              {user.email}
            </div>
          )}
          <div>
            <label className="tsw-label">{t("auth.currentPassword")}</label>
            <input
              type="password"
              autoComplete="current-password"
              className="tsw-input"
              required
              minLength={1}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="cp-current"
            />
          </div>
          <div>
            <label className="tsw-label">{t("auth.newPassword")}</label>
            <input
              type="password"
              autoComplete="new-password"
              className="tsw-input"
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="cp-new"
            />
            <p className="text-xs text-slate-400 mt-1">
              {t("phase2.passwordMinHint")}
            </p>
          </div>
          <div>
            <label className="tsw-label">
              {t("phase2.confirmNewPassword")}
            </label>
            <input
              type="password"
              autoComplete="new-password"
              className="tsw-input"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="cp-confirm"
            />
          </div>
          <button
            type="submit"
            className="tsw-btn-primary w-full"
            disabled={loading}
            data-testid="cp-submit"
          >
            {loading ? t("common.saving") : t("common.save")}
          </button>
        </form>
      </div>
    </PublicLayout>
  );
};

export default ChangePassword;
